import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { FormikHelpers } from 'formik';
import { Button, TextField, TextLink } from '@teachstonellc/component-library';
import { sendSmsCodeForLogin } from 'actions/sms';
import { toastError, toastSuccess } from 'components/Toast';
import { Formik } from 'components/EnhancedForm';
import { useLoginManager } from 'components/LoginManager';

import {
  IconWrapper,
  ButtonWrapper,
  ButtonsWrapper,
  ResendWrapper,
  TextButtonWrapper
} from './Styled';

const validationSchema = yup.object({
  code: yup.string().required(() => i18n.ft(messages.errors.code.required))
});

function VerifyCodeForm() {
  const resendRequest = useAsync();
  const { setStep, emailOrPhone, submitForm } = useLoginManager();

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    submitForm({ user: values }, formik);
  }

  function handleResendCode() {
    resendRequest.run(sendSmsCodeForLogin({ phone_number: emailOrPhone }), {
      onSuccess: () => {
        toastSuccess({ description: i18n.ft(messages.codeSent) });
      },
      onError: (error: any) => {
        toastError({ description: error.message });
      }
    });
  }

  function handleBackToLogin() {
    setStep('login');
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={{ login_phone: emailOrPhone, code: '' }}
      validateOnBlur={false}
    >
      {formik => {
        const isCodeError = Boolean(formik.touched.code && formik.errors.code);

        return (
          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <TextField
              {...formik.getFieldProps('code')}
              autoFocus
              pattern="[0-9]*"
              label={i18n.ft(messages.fields.code)}
              placeholder={i18n.ft(messages.fields.codePlaceholder)}
              error={isCodeError ? formik.errors.code : undefined}
            />

            <ButtonsWrapper>
              <ButtonWrapper>
                <Button
                  fullWidth
                  variant="primary"
                  shape="rounded"
                  type="submit"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  {i18n.ft(messages.submit)}
                </Button>
              </ButtonWrapper>

              <ResendWrapper>
                <TextLink
                  as="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendRequest.isLoading}
                >
                  {resendRequest.isLoading && (
                    <IconWrapper>
                      <i
                        className="fa-solid fa-spinner animate-spin"
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        {i18n.ft(messages.resending)}
                      </span>
                    </IconWrapper>
                  )}

                  {resendRequest.isError && (
                    <IconWrapper>
                      <i
                        className="fa-solid fa-triangle-exclamation text-error"
                        aria-hidden="true"
                      />
                      <span className="sr-only">
                        {i18n.ft(messages.resendFailed)}
                      </span>
                    </IconWrapper>
                  )}

                  {i18n.ft(messages.resend)}
                </TextLink>
              </ResendWrapper>
            </ButtonsWrapper>

            <TextButtonWrapper>
              <TextLink as="button" type="button" onClick={handleBackToLogin}>
                {i18n.ft(messages.back)}
              </TextLink>
            </TextButtonWrapper>
          </form>
        );
      }}
    </Formik>
  );
}

export default VerifyCodeForm;
